<script lang="ts">
  import commentCss from '../../../../css/comments.pcss.json'
  import { createBlobFromDataURI, html2json, serialize, type SgEditorBlock } from "./TextFunctions";
  import type { SgSpoiler } from "../../spoiler";
  import { sgAlert } from "../../../plugins/SgAlert";
  import Toolbar from "./Toolbar.svelte";
  import { wait } from "../../../common/helpers";

  export let value: string = ''
  export let className: string = commentCss['editor-textareas']
  export let uploadImage: (file: File) => Promise<void>
  export let attachGame: (gameId: number, gameInfo: {
    poster: string,
    status: number,
    url: string,
    name: string,
    releaseDate: string
  }) => void
  export let addLink: (url: string) => void
  export let inputName: string = 'comment_text'
  export let placeholder: string = 'Текст комментария'
  // noinspection IncorrectFormatting
  export let saveValue: (value: string) => void = () => {}

  let previousValue = ''
  let blocks: SgEditorBlock[] = [{
    type: 'paragraph',
    text: ''
  }]

  $: {
    if (value && previousValue !== value) {
      blocks = value ? html2json(value) : [{
        type: 'paragraph',
        text: ''
      }]
      previousValue = value
    }
  }
  let refs: Array<HTMLTextAreaElement> = []
  let spoilerRefs: Array<SgSpoiler> = []

  $: {
    spoilerRefs.forEach(ref => {
      ref?.removeEventListener('title-change', changeTitle)
      ref?.addEventListener('title-change', changeTitle)
    })

    if (saveValue) {
      saveValue(serialize(blocks))
    }
  }

  // noinspection JSUnusedGlobalSymbols
  export const reset = () => {
    blocks = [{
      type: 'paragraph',
      text: ''
    }]
  }

  // noinspection JSUnusedGlobalSymbols
  export const focus = () => {
    setTimeout(() => {
      refs[refs.length - 1]?.focus()
    }, 100)
  }

  const parseLinks = async (text: string) => {
    if (!text) {
      return text
    }
    const urlRegex = /https?:\/\/[a-z\dа-яё.\/\-_?=&#%,+:@()]+/gi
    const matches = [...text.matchAll(urlRegex)]
    for (let match of matches) {
      addLink(match[0])
    }
    if (!text) {
      return text
    }
    return text.replace(urlRegex, '')
  }

  // noinspection JSUnusedGlobalSymbols
  export const hasLinks = async () => {
    const value = serialize(blocks)
    const textWithoutLinks = await parseLinks(value)
    if (value === textWithoutLinks) {
      return false
    }
    blocks = html2json(textWithoutLinks)
    sgAlert('В тексте были найдены ссылки.<br>Не знаю, как тебе это удалось.<br>Всё исправили, можешь отправлять ещё раз')
    return true
  }

  // noinspection JSUnusedGlobalSymbols
  export const isEmpty = () => {
    const value = serialize(blocks)
    const strippedValue = value.replace(/(<([^>]+)>)/gi, '')
    return strippedValue.length < 3
  }

  const changeTitle = (e: Event & {
    currentTarget: HTMLElement,
    detail: {
      title: string
    }
  }) => {
    e.stopPropagation()
    const index = e.currentTarget.getAttribute('data-editor-index')
    blocks[index].title = e.detail.title
  }

  const onCompositionEnd = (e: CompositionEvent & {
    currentTarget: HTMLTextAreaElement
  }) => {
    const endsWithNL = e.data.endsWith('\n')

    if (endsWithNL) {
      e.currentTarget.value = e.currentTarget.value.replace(/\n+$/, '')
      addBlock()
    }
  }

  const getCurrentBlockIndex = () => {
    return refs.findIndex(ref => ref === document.activeElement)
  }

  const onKeyDown = (e: KeyboardEvent & {
    currentTarget: HTMLTextAreaElement
  }) => {
    if (e.isComposing) {
      return
    }

    const currentIndex = getCurrentBlockIndex()

    if (!refs[currentIndex] || !blocks[currentIndex] || currentIndex === -1) {
      return
    }

    const cursorPosition = getCursorPosition()
    const selectionRange = getSelectionRange()
    const selectionLength = selectionRange[1] - selectionRange[0]

    if ((e.key === 'Enter') && !e.shiftKey) {
      e.preventDefault()
      if (refs[currentIndex].value === '') {
        return
      }
      const currentValue = refs[currentIndex].value
      if (cursorPosition === currentValue.length) {
        addBlock()
        return
      } else {
        splitBlock(currentIndex, cursorPosition)
        return
      }
    }

    if (e.key === 'Backspace') {
      if (blocks.length === 1) {
        if (blocks[currentIndex].type !== 'paragraph' && cursorPosition === 0) {
          e.preventDefault()
          setType('paragraph')
        }
        return
      }

      if (e.currentTarget.value === '') {
        e.preventDefault()
        removeBlock(currentIndex)
        return
      }
      if (cursorPosition === 0 && selectionLength === 0 && currentIndex > 0) {
        e.preventDefault()
        mergeBackwards(currentIndex)
        return
      }
    }

    if (e.key === 'Delete') {
      if (blocks.length === 1) {
        return
      }
      if (e.currentTarget.value === '' || (cursorPosition >= e.currentTarget.value.length && selectionLength === 0)) {
        e.preventDefault()
        mergeForward(currentIndex)
        return
      }
    }

    if (['ArrowLeft', 'ArrowUp'].includes(e.key) && !e.shiftKey) {
      if (currentIndex !== 0 && cursorPosition === 0) {
        e.preventDefault()
        const pos = refs[currentIndex - 1].value.length
        setSelection(currentIndex - 1, pos)
      }
    }

    if (['ArrowRight', 'ArrowDown'].includes(e.key) && !e.shiftKey) {
      if (currentIndex !== blocks.length - 1 && cursorPosition === refs[currentIndex].value.length) {
        e.preventDefault()
        setSelection(currentIndex + 1, 0)
      }
    }
  }

  const getCursorPosition = () => {
    const textarea = refs[getCurrentBlockIndex()]
    if (!textarea) {
      return 0
    }
    return textarea.selectionStart
  }

  const getSelectionRange = () => {
    const textarea = refs[getCurrentBlockIndex()]
    if (!textarea) {
      return [0, 0]
    }
    return [textarea.selectionStart, textarea.selectionEnd]
  }

  const mergeBackwards = async (index: number) => {
    const prevLength = blocks[index - 1].text.length
    blocks[index - 1].text += blocks[index].text
    blocks.splice(index, 1)
    await wait(100)
    setSelection(index - 1, prevLength)
  }

  const splitBlock = async (index: number, pos: number) => {
    const newBlock = {
      type: 'paragraph',
      text: blocks[index].text.substring(pos)
    }
    blocks[index].text = blocks[index].text.substring(0, pos)
    blocks.splice(index + 1, 0, newBlock)
    await wait(100)
    setSelection(index + 1, 0)
  }

  const setSelection = (index: number, start: number, end: number = null) => {
    if (end === null) {
      end = start
    }

    const textarea = refs[index]
    textarea.focus()
    textarea.setSelectionRange(start, end)
  }

  const mergeForward = (index: number) => {
    if (index >= blocks.length - 1) {
      return
    }
    const blockLength = blocks[index].text.length
    blocks[index].text += blocks[index + 1].text
    blocks.splice(index + 1, 1)
    setSelection(index, blockLength)
  }

  const addBlock = (type: string = 'paragraph', text: string = '', focus: boolean = true) => {
    let index = getCurrentBlockIndex()
    blocks.splice(index + 1, 0, {
      type,
      title: type === 'spoiler' ? 'Скрытый текст' : undefined,
      text
    })
    blocks = [...blocks]
    if (focus) {
      setTimeout(() => {
        refs[index + 1]?.focus()
      }, 100)
    }
  }

  const removeBlock = (index: number, focus: boolean = true) => {
    blocks.splice(index, 1)
    blocks = [...blocks]
    if (focus) {
      const newIndex = index === 0 ? 0 : index - 1
      setTimeout(() => {
        refs[newIndex]?.focus()
      }, 100)
    }
  }

  const setType = async (type: string) => {
    const index = getCurrentBlockIndex()
    blocks[index].type = type
    if (type === 'spoiler') {
      blocks[index].title = 'Скрытый текст'
    } else if (blocks[index].title) {
      delete blocks[index].title
    }
    await wait(100)
    focusBlock(index)
  }

  const focusBlock = (index: number) => {
    refs[index]?.focus()
    let textLength = refs[index].value.length ?? 0
    setSelection(index, textLength)
  }

  const paste = (text: string) => {
    if (!text || text == '') {
      return
    }
    const textArray = text.split('\n').filter(paragraph => paragraph.trim() !== '');
    let currentIndex = getCurrentBlockIndex()
    const range = getSelectionRange()
    const selectionStart = range[0]
    const selectionEnd = range[1]
    let lastLineLength = textArray[textArray.length - 1]?.length ?? 1
    if (textArray.length === 1) {
      lastLineLength += selectionStart
    }
    const sourceTextStart = blocks[currentIndex].text.substring(0, selectionStart)
    const sourceTextEnd = blocks[currentIndex].text.substring(selectionEnd)
    const currentType = blocks[currentIndex].type
    textArray[0] = sourceTextStart + textArray[0]
    textArray[textArray.length - 1] = textArray[textArray.length - 1] + sourceTextEnd
    blocks.splice(currentIndex, 1, ...textArray.map(paragraphText => ({
      type: currentType,
      text: paragraphText
    })))
    refs[currentIndex].value = textArray[0]
    refs[currentIndex].dispatchEvent(new Event('input', { bubbles: true }))
    const newIndex = currentIndex + (textArray.length - 1)
    setTimeout(() => {
      setSelection(newIndex, lastLineLength, lastLineLength)
    }, 100)
  }

  const onPaste = (e: ClipboardEvent & {
    dataTransfer?: DataTransfer
  }) => {
    e.preventDefault()
    let items = e.clipboardData?.items || e.dataTransfer?.items || []
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      switch (item.kind) {
        case 'file':
          if (item.type.includes('image/') && item.type !== 'image/svg') {
            const file = item.getAsFile()
            uploadImage(file)
          }
          break;
        case 'string':
          if (item.type === 'text/plain') {
            item.getAsString(async (data: string) => {
              if (/^data:image\//.test(data)) {
                const file = createBlobFromDataURI(data)
                // noinspection ES6MissingAwait
                uploadImage(file)
                return
              }
              let newText = await parseLinks(data)
              paste(newText)
            })
          }
          break
      }
    }
  }

  const toggleBlock = (type: string) => {
    const index = getCurrentBlockIndex()
    if (!blocks[index]) {
      addBlock(type)
      return
    }

    if (blocks[index].type === type) {
      setType('paragraph')
    } else {
      setType(type)
    }
    refs[index].focus()
  }
</script>

<div class={className}>
  {#each blocks as block, index (index)}
    {#if block.type === 'quote'}
      <blockquote data-replicated-value={block.text}>
                <textarea
                  bind:this={refs[index]}
                  bind:value={block.text}
                  class={commentCss['editor-textarea']}
                  placeholder={placeholder}
                  on:keydown={onKeyDown}
                  on:compositionend={onCompositionEnd}
                  on:paste={onPaste}
                  rows="1"
                />
      </blockquote>
    {:else if block.type === 'spoiler'}
      <sg-spoiler
        title={block.title}
        bind:this={spoilerRefs[index]}
        data-editor-index={index}
        data-editable='true'
        data-open
      >
        <div data-replicated-value={block.text}>
          <textarea
            bind:this={refs[index]}
            bind:value={block.text}
            class={commentCss['editor-textarea']}
            placeholder={placeholder}
            on:keydown={onKeyDown}
            on:compositionend={onCompositionEnd}
            on:paste={onPaste}
            rows="1"
          />
        </div>
      </sg-spoiler>
    {:else}
      <div data-replicated-value={block.text}>
                <textarea
                  bind:this={refs[index]}
                  bind:value={block.text}
                  class={commentCss['editor-textarea']}
                  placeholder={placeholder}
                  on:keydown={onKeyDown}
                  on:compositionend={onCompositionEnd}
                  on:paste={onPaste}
                  rows="1"
                />
      </div>
    {/if}
  {/each}
  <input type="hidden" name={inputName} value={serialize(blocks)}/>
</div>
<Toolbar
  uploadImage={uploadImage}
  toggleBlock={toggleBlock}
  attachGame={attachGame}
/>
